import React, { useState, useEffect } from 'react';
import { setErrorDisplayCallback, initGlobalErrorHandlers } from '../utils/errorHandler';

/**
 * Компонент для отображения ошибок на весь экран
 */
export function ErrorDisplay() {
  const [errors, setErrors] = useState([]);
  const [currentErrorIndex, setCurrentErrorIndex] = useState(0);

  useEffect(() => {
    // Инициализация глобальных обработчиков
    initGlobalErrorHandlers();

    // Установка callback для отображения ошибок
    setErrorDisplayCallback((errorInfo) => {
      setErrors(prev => [...prev, errorInfo]);
    });

    return () => {
      setErrorDisplayCallback(null);
    };
  }, []);

  const currentError = errors[currentErrorIndex];

  if (errors.length === 0) {
    return null;
  }

  const closeError = () => {
    setErrors([]);
    setCurrentErrorIndex(0);
  };

  const nextError = () => {
    if (currentErrorIndex < errors.length - 1) {
      setCurrentErrorIndex(prev => prev + 1);
    }
  };

  const prevError = () => {
    if (currentErrorIndex > 0) {
      setCurrentErrorIndex(prev => prev - 1);
    }
  };

  const copyToClipboard = () => {
    const text = formatErrorAsText(currentError);
    navigator.clipboard.writeText(text).then(() => {
      alert('Ошибка скопирована в буфер обмена!');
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-red-900 text-white overflow-auto p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-red-800 p-4 rounded-t-lg flex items-center justify-between sticky top-0">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🚨</span>
            <div>
              <h1 className="text-2xl font-bold">ОШИБКА ПРИЛОЖЕНИЯ</h1>
              <p className="text-sm opacity-90">
                {errors.length > 1 
                  ? `Ошибка ${currentErrorIndex + 1} из ${errors.length}`
                  : 'Сделайте скриншот и отправьте разработчику'
                }
              </p>
            </div>
          </div>
          <button
            onClick={closeError}
            className="bg-white text-red-900 px-6 py-2 rounded-lg font-bold hover:bg-gray-100 transition-colors"
          >
            ✕ Закрыть
          </button>
        </div>

        {/* Error Content */}
        <div className="bg-red-800 p-6 space-y-4">
          {/* Timestamp */}
          <div className="bg-red-700 p-3 rounded">
            <div className="text-xs opacity-75 mb-1">Время ошибки:</div>
            <div className="font-mono text-lg">
              {new Date(currentError.timestamp).toLocaleString('ru-RU')}
            </div>
          </div>

          {/* Error Message */}
          <div className="bg-red-700 p-3 rounded">
            <div className="text-xs opacity-75 mb-1">Сообщение об ошибке:</div>
            <div className="font-bold text-xl break-words">
              {currentError.message}
            </div>
          </div>

          {/* Error Name */}
          {currentError.name && (
            <div className="bg-red-700 p-3 rounded">
              <div className="text-xs opacity-75 mb-1">Тип ошибки:</div>
              <div className="font-mono text-lg">
                {currentError.name}
              </div>
            </div>
          )}

          {/* Status Code */}
          {currentError.status && (
            <div className="bg-red-700 p-3 rounded">
              <div className="text-xs opacity-75 mb-1">HTTP Статус:</div>
              <div className="font-mono text-2xl font-bold">
                {currentError.status}
              </div>
            </div>
          )}

          {/* Error Data */}
          {currentError.data && Object.keys(currentError.data).length > 0 && (
            <div className="bg-red-700 p-3 rounded">
              <div className="text-xs opacity-75 mb-1">Данные ошибки:</div>
              <pre className="font-mono text-sm overflow-auto max-h-40 bg-red-900 p-2 rounded">
                {JSON.stringify(currentError.data, null, 2)}
              </pre>
            </div>
          )}

          {/* Context */}
          {currentError.context && Object.keys(currentError.context).length > 0 && (
            <div className="bg-red-700 p-3 rounded">
              <div className="text-xs opacity-75 mb-1">Контекст:</div>
              <pre className="font-mono text-sm overflow-auto max-h-40 bg-red-900 p-2 rounded">
                {JSON.stringify(currentError.context, null, 2)}
              </pre>
            </div>
          )}

          {/* Stack Trace */}
          {currentError.stack && (
            <div className="bg-red-700 p-3 rounded">
              <div className="text-xs opacity-75 mb-1">Stack Trace:</div>
              <pre className="font-mono text-xs overflow-auto max-h-60 bg-red-900 p-2 rounded whitespace-pre-wrap break-words">
                {currentError.stack}
              </pre>
            </div>
          )}
        </div>

        {/* Footer with Actions */}
        <div className="bg-red-800 p-4 rounded-b-lg sticky bottom-0 space-y-3">
          {/* Navigation buttons if multiple errors */}
          {errors.length > 1 && (
            <div className="flex gap-2">
              <button
                onClick={prevError}
                disabled={currentErrorIndex === 0}
                className="flex-1 bg-red-700 px-4 py-3 rounded-lg font-bold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Предыдущая ошибка
              </button>
              <button
                onClick={nextError}
                disabled={currentErrorIndex === errors.length - 1}
                className="flex-1 bg-red-700 px-4 py-3 rounded-lg font-bold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Следующая ошибка →
              </button>
            </div>
          )}

          {/* Copy button */}
          <button
            onClick={copyToClipboard}
            className="w-full bg-yellow-500 text-yellow-900 px-4 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors"
          >
            📋 Скопировать текст ошибки
          </button>

          <div className="text-center text-sm opacity-75">
            Сделайте скриншот этого экрана и отправьте разработчику
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Форматирует ошибку в текст для копирования
 */
function formatErrorAsText(error) {
  let text = '=== ОШИБКА ПРИЛОЖЕНИЯ ===\n\n';
  text += `Время: ${new Date(error.timestamp).toLocaleString('ru-RU')}\n\n`;
  text += `Сообщение: ${error.message}\n\n`;
  
  if (error.name) {
    text += `Тип: ${error.name}\n\n`;
  }
  
  if (error.status) {
    text += `HTTP Статус: ${error.status}\n\n`;
  }
  
  if (error.data && Object.keys(error.data).length > 0) {
    text += `Данные:\n${JSON.stringify(error.data, null, 2)}\n\n`;
  }
  
  if (error.context && Object.keys(error.context).length > 0) {
    text += `Контекст:\n${JSON.stringify(error.context, null, 2)}\n\n`;
  }
  
  if (error.stack) {
    text += `Stack Trace:\n${error.stack}\n`;
  }
  
  return text;
}
